import type React from 'react';
import type { TFunction } from 'i18next';
import type { ShiftSummary } from '../../../../../services/simulation/runReport';
import type { RunReport } from '../useRunReport';
import { opsColors } from '../../../../../theme/opsTokens';
import { formatNumber } from './reportFormat';

export type KmLabel = { readonly kind: 'loading' } | { readonly kind: 'text'; readonly text: string };

export function vehicleName(shift: ShiftSummary, t: TFunction): string {
  return t('results.report.vehicleName', { number: String(shift.ordinal).padStart(2, '0') });
}

export function seatLabel(shift: ShiftSummary, t: TFunction): string {
  return shift.seats.wheelchair > 0
    ? t('results.report.rail.seatsWithWheelchair', { standard: shift.seats.standard, wheelchair: shift.seats.wheelchair })
    : t('results.report.rail.seats', { standard: shift.seats.standard });
}

export function percentLabel(ratio: number | null, t: TFunction, locale: string): string {
  return ratio == null ? t('results.report.noValue') : t('results.report.units.percentValue', { value: formatNumber(ratio * 100, locale) });
}

/** Road km waits for `/paths`, which loads after everything else. */
export function kmLabel(report: RunReport, shiftId: string, t: TFunction, locale: string): KmLabel {
  if (report.paths.status === 'idle' || report.paths.status === 'loading') return { kind: 'loading' };
  const km = report.distances?.kmByShift.get(shiftId);
  return { kind: 'text', text: km == null ? t('results.report.noValue') : formatNumber(km, locale, 1) };
}

/** Click and Enter/Space handlers that make a table row act as a button. */
export function rowActivation(activate: () => void) {
  return {
    tabIndex: 0,
    onClick: activate,
    onKeyDown: (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
      activate();
    },
    role: 'button',
    sx: { cursor: 'pointer', '&:focus-visible': { outline: `2px solid ${opsColors.brand.primary}`, outlineOffset: '-2px' } },
  };
}
